import React, { useState, useEffect, useImperativeHandle } from 'react';
import Box from '@mui/material/Box';
import { SxProps, Theme } from '@mui/system';
import Skeleton from '@mui/material/Skeleton';

import CheckboxCustom from './groupCheckbox/CheckboxCustom';
import { getToken } from 'src/utils/auth';
import { useStore } from 'src/context/StoreContext';
import { getAPI, postAPI } from 'src/utils/api';
import { getField } from 'src/common/helper';

type GroupCheckboxProps = {
  selecteds?: string[];
  sx?: SxProps<Theme>;
  onChange?: (selecteds: string[]) => void;
  event?: () => void;
  ref: React.MutableRefObject<undefined>;
  onReload?: () => void;
};

type Data = {
  detail: number;
  calendar_days: number;
  machines?: number[];
  nurse?: number;
  reception?: number;
  doctors?: number[];
};

const GroupCheckbox = React.forwardRef<
  unknown,
  Omit<GroupCheckboxProps, 'ref'>
>((props, ref) => {
  const storeContext = useStore();
  const optionFilter = storeContext.optionFilter;
  const tabCalendar = storeContext.tabCalendar;
  const setOptionFilterSelected = storeContext.setOptionFilterSelected;
  const optionFilterSelected = storeContext.optionFilterSelected;

  const [filter, setFilter] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const id = event.target.value;
    const checked = event.target.checked;

    let newArray = !checked
      ? filter.filter((number) => number !== id)
      : [...filter, id];

    setFilter(newArray);
    handleSaveSetting(newArray);
    props.onChange?.(newArray);
  };

  const handleSaveSetting = async (currentFilter: string[]) => {
    let dataChange = {
      doctors: [] as number[],
      machines: [] as number[],
      nurse: 0,
      reception: 0,
    };

    currentFilter.forEach((value) => {
      const [key, numStr] = value.split('-');
      const num = Number(numStr);

      switch (key) {
        case 'doctor':
          dataChange.doctors.push(num);
          break;
        case 'machine':
          dataChange.machines.push(num);
          break;
        case 'nurse':
          dataChange.nurse = 1;
          break;
        case 'reception':
          dataChange.reception = 1;
          break;
        default:
          break;
      }
    });
    if (tabCalendar === 'calendar') {
      await saveSetting(dataChange);
    } else {
      setOptionFilterSelected(dataChange.doctors);
      props.onReload?.();
    }
  };

  const saveSetting = async (dataChange: any) => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      let data: Data = {
        detail: 1,
        calendar_days: 30,
      };

      if (dataChange['doctors'].length !== 0) {
        data = {
          ...data,
          doctors: dataChange['doctors'],
        };
      }

      if (dataChange['machines'].length !== 0) {
        data = {
          ...data,
          machines: dataChange['machines'],
        };
      }

      if (dataChange['nurse'] !== 0) {
        data = {
          ...data,
          nurse: 1,
        };
      }

      if (dataChange['reception'] !== 0) {
        data = {
          ...data,
          reception: 1,
        };
      }

      postAPI(`/calendar/save-filter?token=${token}`, data)
        .then((res: any) => {
          if (res.code === 200) {
          }
        })
        .finally(() => {
          props.onReload?.();
        });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const getSetting = async () => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      getAPI(`/calendar/get-config?token=${token}`)
        .then((res: any) => {
          if (res.code === 200 && res.data && res.data.settings) {
            convertConfigToFilter(res.data.settings);
          }
        })
        .finally(() => {});
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const convertConfigToFilter = (data: any) => {
    const newArray: string[] = [];

    if (data.doctors) {
      data.doctors.forEach((id: number) => {
        newArray.push(`doctor-${id}`);
      });
    }

    if (data.machines) {
      data.machines.forEach((id: number) => {
        newArray.push(`machine-${id}`);
      });
    }

    if (data.nurse) {
      newArray.push(`nurse-5`);
    }

    if (data.reception) {
      newArray.push(`reception-6`);
    }

    setFilter(newArray);
  };

  const getSettingSchedule = () => {
    const newArray: string[] = [];

    for (let id of optionFilterSelected) {
      newArray.push(`doctor-${id}`);
    }

    setFilter(newArray);
  };

  useEffect(() => {
    if (optionFilter.length !== 0) {
      setLoading(false);
    }
  }, [optionFilter]);

  useEffect(() => {
    if (tabCalendar === 'calendar') {
      getSetting();
    } else {
      getSettingSchedule();
    }
    // eslint-disable-next-line
  }, [tabCalendar]);

  useImperativeHandle(ref, () => ({
    async handleClear() {
      if (filter.length !== 0) {
        await handleSaveSetting([]);
      }

      setFilter([]);
    },

    // async handleSaveSetting() {
    //   let dataChange = {
    //     doctors: [] as number[],
    //     machines: [] as number[],
    //     nurse: 0,
    //     reception: 0,
    //   };

    //   filter.forEach((value) => {
    //     const [key, numStr] = value.split('-');
    //     const num = Number(numStr);

    //     switch (key) {
    //       case 'doctor':
    //         dataChange.doctors.push(num);
    //         break;
    //       case 'machine':
    //         dataChange.machines.push(num);
    //         break;
    //       case 'nurse':
    //         dataChange.nurse = 1;
    //         break;
    //       case 'reception':
    //         dataChange.reception = 1;
    //         break;
    //       default:
    //         break;
    //     }
    //   });
    //   if (tabCalendar === 'calendar') {
    //     await saveSetting(dataChange);
    //   } else {
    //     setOptionFilterSelected(dataChange.doctors);
    //   }
    // },
  }));

  return (
    <Box
      sx={{
        width: 'max-content',
        ...props.sx,
      }}
    >
      {!loading ? (
        optionFilter.map((value, index) => (
          <Box key={index}>
            <CheckboxCustom
              value={value.type + '-' + value.id}
              label={value.name}
              checked={filter.includes(value.type + '-' + value.id)}
              onChange={handleChange}
              sx={{ 
                mt: '12px',
                '& .MuiFormControlLabel-root': {
                  '& .Mui-checked': { color: getField(value, 'color', '') }
                }
              }}
            />
          </Box>
        ))
      ) : (
        <Box>
          <Skeleton variant='rectangular' width={150} height={30} />
          <Skeleton
            variant='rectangular'
            width={150}
            height={30}
            sx={{ mt: '10px' }}
          />
          <Skeleton
            variant='rectangular'
            width={150}
            height={30}
            sx={{ mt: '10px' }}
          />
          <Skeleton
            variant='rectangular'
            width={150}
            height={30}
            sx={{ mt: '10px' }}
          />
        </Box>
      )}
    </Box>
  );
});

export default GroupCheckbox;
